// ----- Phase 5.25.5 — Import API client ----- //
//
// Kept in `web/src/lib/api/` (alongside `compiler.ts`) rather than
// folded into `v2Client.ts` so the Import surface can grow with the
// parser sub-phases (5.25.1 XML, 5.25.2 ZIP, 5.25.3 FDA-ESG) without
// bloating the main client.
//
// `commit()` is the convergence point for all three parser tabs:
// each tab parses its source format into the `ImportParsedData` shape,
// the operator reviews + edits in `ImportPreviewPanel`, then the panel's
// `onCommit` callback invokes this method. This phase ships only the
// client method (and its backend) — no tab is wired yet (Phase 5.25
// kept ImportPage as a UI shell; the parser sub-phases will wire each
// tab's `onCommit`).

import { apiFetch } from '@/api/v2Client';
import type { ImportParsedData } from '@/components/import/ImportPreviewPanel';

export interface ImportCommitResult {
  /**
   * IDs of created Submissions rows, in the same order as the input
   * `sequences` array.
   */
  readonly submissionIds: readonly string[];
}

export const importApi = {
  commit: async (data: ImportParsedData): Promise<ImportCommitResult> => {
    return apiFetch<ImportCommitResult>('/api/v1/import/commit', {
      method: 'POST',
      json: data,
    });
  },

  /**
   * Phase 5.25.1 — POST multipart upload of one or more eCTD XML
   * files. Returns the same `ImportParsedData` shape that
   * `ImportPreviewPanel` consumes. Browser sets the multipart
   * boundary automatically when `body` is a `FormData` — do NOT set
   * Content-Type manually (per browser spec; setting it would
   * replace the auto-boundary header and break the upload).
   */
  parseXml: async (files: File[]): Promise<ImportParsedData> => {
    const form = new globalThis.FormData();
    files.forEach((f, i) => {
      form.append(`file${i}`, f, f.name);
    });
    return apiFetch<ImportParsedData>('/api/v1/import/parse-xml', {
      method: 'POST',
      body: form,
    });
  },

  /**
   * Phase 5.25.2 — POST multipart upload of a single eCTD ZIP package.
   * Returns the same `ImportParsedData` shape that `parseXml` does —
   * the route extracts backbone XMLs from the ZIP in-memory and runs
   * them through Phase 5.25.1's parser, then forces `summary.source =
   * 'zip'`. As with `parseXml`, do NOT set Content-Type manually.
   */
  parseZip: async (file: File): Promise<ImportParsedData> => {
    const form = new globalThis.FormData();
    form.append('file', file, file.name);
    return apiFetch<ImportParsedData>('/api/v1/import/parse-zip', {
      method: 'POST',
      body: form,
    });
  },

  /**
   * Phase 5.25.3 — GET openFDA-sourced submission history for an
   * application number. Backend queries openFDA across NDA/ANDA/BLA
   * prefixes; result is cached for 24h. Returns the same
   * `ImportParsedData` shape (`summary.source = 'fda'`) so the preview
   * panel handles all import sources uniformly. Backend returns 404
   * for unknown applications — caller should catch and surface a
   * friendly message rather than the raw ApiError body.
   */
  lookupFda: async (applicationNumber: string): Promise<ImportParsedData> => {
    const encoded = encodeURIComponent(applicationNumber);
    return apiFetch<ImportParsedData>(`/api/v1/import/fda-esg/${encoded}`, {
      method: 'GET',
    });
  },
};
